import { toast } from "sonner";
import { defaultSettings } from "@/context/SettingsContext";
import { useOnboarding } from "@/context/OnboardingContext";
import config from "@/config";

export default function useUserInteractionHandling() {
  const {
    state,
    setState,
    likedIdeas,
    setLikedIdeas,
    setExpandedSteps,
    setSelectedIntegrations,
  } = useOnboarding();

  // Handle thumbs up on content idea
  const handleThumbsUp = (id: string) => {
    setLikedIdeas((prev) => [...prev, id]);

    // Mark idea as liked and hide it
    const postIdeas = JSON.parse(localStorage.getItem("post_ideas") || "[]");
    const updatedIdeas = postIdeas.map((idea: any) =>
      idea.id === id ? { ...idea, liked: true, hidden: true } : idea
    );
    localStorage.setItem("post_ideas", JSON.stringify(updatedIdeas));

    // Force update
    setState((prev) => ({ ...prev }));

    toast.success(
      "Idea added to your planned content! We'll generate content based on this topic."
    );
  };

  // Handle thumbs down on content idea
  const handleThumbsDown = (id: string) => {
    setLikedIdeas((prev) => prev.filter((ideaId) => ideaId !== id));

    // Mark idea as disliked and hide it
    const postIdeas = JSON.parse(localStorage.getItem("post_ideas") || "[]");
    const updatedIdeas = postIdeas.map((idea: any) =>
      idea.id === id ? { ...idea, liked: false, hidden: true } : idea
    );
    localStorage.setItem("post_ideas", JSON.stringify(updatedIdeas));

    // Force update
    setState((prev) => ({ ...prev }));
  };

  // Toggle expanded state for a step
  const handleToggleStepExpanded = (stepId: number) => {
    setExpandedSteps((prev) => ({
      ...prev,
      [stepId]: !prev[stepId],
    }));
  };

  // Helper function to update URL parameters
  const handleUpdateUrlParams = (params: Record<string, string>) => {
    const searchParams = new URLSearchParams(window.location.search);

    // Update each parameter
    Object.entries(params).forEach(([key, value]) => {
      if (value) {
        searchParams.set(key, value);
      } else {
        searchParams.delete(key);
      }
    });

    // Update URL without reloading the page
    window.history.replaceState(
      {},
      "",
      `${window.location.pathname}?${searchParams.toString()}`
    );
  };

  // Move to scheduling step
  const handleContinueToScheduling = async () => {
    // Update onboarding analytics with generated content and status
    try {
      const website_id = localStorage.getItem("website_id");
      const website_url = localStorage.getItem("onboardingWebsite");
      const generatedContent = JSON.parse(
        localStorage.getItem("generated_content") || "{}"
      );

      await fetch(
        `${config.supabase.functionsUrl}/store-onboarding-analytics`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            website_id,
            website_url,
            status: "post_accepted",
            post_theme_content: {
              title: generatedContent.title,
              content: generatedContent.content,
              status: "accepted",
              created_at: generatedContent.created_at,
            }, // Store the actual generated article here
          }),
        }
      );
    } catch (error) {
      console.error("Error updating onboarding analytics:", error);
    }

    // Update status to schedule_shown
    try {
      const website_id = localStorage.getItem("website_id");
      const website_url = localStorage.getItem("onboardingWebsite");

      await fetch(
        `${config.supabase.functionsUrl}/store-onboarding-analytics`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            website_id,
            website_url,
            status: "schedule_shown",
          }),
        }
      );
    } catch (error) {
      console.error("Error updating onboarding analytics:", error);
    }

    setState((prev) => ({
      ...prev,
      step: 4,
      progress: 0,
    }));

    // Update URL to reflect current step
    handleUpdateUrlParams({ step: "scheduling" });
  };

  // Handle posting day toggle
  const handleDayToggle = (day: string) => {
    setState((prev) => {
      const newDays = prev.postingDays.includes(day)
        ? prev.postingDays.filter((d) => d !== day)
        : [...prev.postingDays, day];

      // Ensure we don't exceed the posting frequency
      if (newDays.length > prev.postingFrequency) {
        toast.warning(
          `You can only select ${prev.postingFrequency} days for your current frequency.`
        );
        return prev;
      }

      return {
        ...prev,
        postingDays: newDays,
      };
    });
  };

  // Handle continue to WordPress auth
  const handleContinueToWordPress = () => {
    setState((prev) => ({ ...prev, step: 5 })); // Move to WordPress auth
    handleUpdateUrlParams({ step: "auth" });
  };

  // Publish content and complete
  const handlePublishContent = () => {
    setState((prev) => ({ ...prev, step: 4 })); // Now goes to scheduling step
    handleUpdateUrlParams({ step: "scheduling" });
  };

  // Add WordPress connection handlers
  const handleSkipWordPress = () => {
    toast.info(
      "Content saved as draft. You can connect WordPress later from the settings."
    );
    setState((prev) => ({ ...prev, step: 5 })); // Go to completion

    // Update URL to reflect current step
    handleUpdateUrlParams({ step: "integration" });
  };

  const handleStartWordPressAuth = () => {
    setState((prev) => ({ ...prev, showWpAuthDialog: true }));
  };

  const handleCompleteWordPressAuth = async () => {
    if (!state.wpUsername || !state.wpPassword) {
      setState((prev) => ({
        ...prev,
        wpConnectionError: "Please enter both username and application password",
      }));
      return;
    }

    setState((prev) => ({
      ...prev,
      isAuthenticating: true,
      wpConnectionError: null,
    }));

    try {
      // Store credentials in sessionStorage
      const wpCredentials = {
        website_url: state.websiteUrl,
        username: state.wpUsername,
        password: state.wpPassword,
        created_at: new Date().toISOString(),
      };
      sessionStorage.setItem("wp_credentials", JSON.stringify(wpCredentials));

      // Test the connection using the Edge Function
      const response = await fetch(
        `${config.supabase.functionsUrl}/wordpress-proxy`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            wpUrl: state.websiteUrl,
            username: state.wpUsername,
            password: state.wpPassword,
          }),
        }
      );

      const data = await response.json();

      if (data.success) {
        toast.success("WordPress connected successfully!");
        setState((prev) => ({
          ...prev,
          showWpAuthDialog: false,
          isAuthenticating: false,
        }));
        // Move to completion step
        setState((prev) => ({ ...prev, step: 5 }));
      } else {
        throw new Error(data.error || "Failed to connect to WordPress");
      }
    } catch (error) {
      console.error("WordPress connection error:", error);
      setState((prev) => ({
        ...prev,
        isAuthenticating: false,
        wpConnectionError: error.message || "Failed to connect to WordPress",
      }));
    }
  };

  // Update handleContinueToAuth to send scheduling settings
  const handleContinueToAuth = async () => {
    // Format posting days to match database structure
    const formattedPostingDays = state.postingDays.reduce<
      Array<{ day: string; count: number }>
    >((acc, day) => {
      const existingDay = acc.find((d) => d.day === day);
      if (existingDay) {
        existingDay.count += 1;
      } else {
        acc.push({ day, count: 1 });
      }
      return acc;
    }, []);

    // Save publication settings
    const publicationSettings = {
      posting_frequency: state.postingFrequency,
      posting_days: formattedPostingDays,
      website_id: localStorage.getItem("website_id"),
      organisation_id: localStorage.getItem("organisation_id"),
      writing_style: defaultSettings.writingStyle,
      subject_matters: [],
    };

    localStorage.setItem("publication_settings", JSON.stringify(publicationSettings));

    // Update onboarding analytics with scheduling settings and status
    try {
      const website_id = localStorage.getItem("website_id");
      const website_url = localStorage.getItem("onboardingWebsite");

      await fetch(
        `${config.supabase.functionsUrl}/store-onboarding-analytics`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            website_id,
            website_url,
            status: "schedule_accepted",
            scheduling_settings: publicationSettings,
          }),
        }
      );
    } catch (error) {
      console.error("Error updating onboarding analytics:", error);
    }

    // Show signup modal
    setState((prev) => ({ ...prev, showSignupModal: true }));
  };

  // Add handler for integration selection
  const handleIntegrationToggle = (groupId: string, optionId: string) => {
    setSelectedIntegrations((prev) => {
      const group = prev[groupId] || [];
      return {
        ...prev,
        [groupId]: group.includes(optionId)
          ? group.filter((id) => id !== optionId)
          : [...group, optionId],
      };
    });
  };

  // Check if any ideas are liked
  const handleHasLikedIdeas = () => likedIdeas.length > 0;

  return {
    handleThumbsUp,
    handleThumbsDown,
    handleToggleStepExpanded,
    handleUpdateUrlParams,
    handleContinueToScheduling,
    handleDayToggle,
    handleContinueToWordPress,
    handlePublishContent,
    handleSkipWordPress,
    handleStartWordPressAuth,
    handleCompleteWordPressAuth,
    handleContinueToAuth,
    handleIntegrationToggle,
    handleHasLikedIdeas,
  };
}
